import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { DEMO_CPT_CODES, DEMO_ICD10_CODES, decide, type ChargeResponse } from '@emr/shared';

import { createCharge, fetchCharges } from '../api/rcm.ts';
import { listEncounters } from '../api/encounters.ts';
import { useGrants } from '../auth/sessionContext.ts';
import { Button, ErrorText, Field, Panel } from '../components/primitives.tsx';
import { DataTable, type DataTableColumn } from '../components/DataTable.tsx';
import { GatedRoute } from '../components/GatedRoute.tsx';
import { QuerySection } from '../components/QuerySection.tsx';
import { formatDateTime } from '../lib/dateFormat.ts';

/**
 * Charge capture (`BACKLOG.md` R1) — the patient-scoped surface for entering CPT/ICD-10 coding
 * against a visit and seeing the resulting billed amount. Gated on `billing:manage` alone
 * (`chargesRoutes.ts`'s own header explains why this is narrower than aesthetics' `patient:read` |
 * `billing:manage` billing routes), no `feature` gate — RCM is base functionality, not a
 * `COMMERCIAL_FEATURES`-gated specialty module.
 *
 * The CPT/ICD-10 pickers are the demo catalog `@emr/shared`'s `rcm.ts` exports, not a licensed code
 * database — that file's own header explains why. `chargeAmountCents` is never computed client-side
 * and submitted; the server prices it from `cptCode`/`units` against the same catalog.
 */
export function PatientRcmChargesPage() {
  const { t } = useTranslation('rcm');
  const { patientId } = useParams<{ patientId: string }>();

  if (patientId === undefined) {
    return <p className="m-0 text-sm text-danger">{t('common.noPatientId')}</p>;
  }

  return (
    <GatedRoute title={t('charges.pageTitle')} capability="billing:manage" feature={null}>
      <ChargesSection patientId={patientId} />
    </GatedRoute>
  );
}

function columnsFor(
  t: ReturnType<typeof useTranslation<'rcm'>>['t'],
): readonly DataTableColumn<ChargeResponse>[] {
  return [
    {
      key: 'cptCode',
      header: t('charges.columns.cptCode'),
      cell: (charge) => charge.cptCode,
      wrap: 'nowrap',
      cellClassName: 'font-mono',
    },
    {
      key: 'icd10Codes',
      header: t('charges.columns.icd10Codes'),
      cell: (charge) => charge.icd10Codes.join(', '),
      wrap: 'anywhere',
      maxWidthCh: 32,
      cellClassName: 'font-mono',
    },
    {
      key: 'units',
      header: t('charges.columns.units'),
      cell: (charge) => charge.units,
      wrap: 'nowrap',
    },
    {
      key: 'amount',
      header: t('charges.columns.amount'),
      cell: (charge) => `$${(charge.chargeAmountCents / 100).toFixed(2)}`,
      wrap: 'nowrap',
    },
    {
      key: 'status',
      header: t('charges.columns.status'),
      cell: (charge) => charge.status,
      wrap: 'nowrap',
    },
    {
      // `BACKLOG.md` M3a items 1-2: the blocking-vs-warning contract, surfaced — a blocking `errors`
      // entry is why a line is stuck `draft` (`chargeStatusForIssues`); a `warnings` entry never
      // blocks, it is only worth a look before submission (`charges.description`'s own self-pay note).
      key: 'issues',
      header: t('charges.columns.issues'),
      cell: (charge) => (
        <>
          {charge.errors.map((message, index) => (
            <p key={`error-${String(index)}`} className="m-0 text-danger">
              {message}
            </p>
          ))}
          {charge.warnings.map((message, index) => (
            <p key={`warning-${String(index)}`} className="m-0 text-warning">
              {message}
            </p>
          ))}
        </>
      ),
      wrap: 'wrap',
      maxWidthCh: 40,
    },
    {
      key: 'createdAt',
      header: t('charges.columns.createdAt'),
      cell: (charge) => formatDateTime(charge.createdAt),
      wrap: 'nowrap',
    },
  ];
}

function ChargesSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('rcm');
  const grants = useGrants();
  const mayWrite = decide(grants, 'billing:manage');
  const queryClient = useQueryClient();
  const queryKey = ['rcm', 'charges', patientId];

  const query = useQuery<readonly ChargeResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchCharges(patientId, signal),
  });

  const encountersQuery = useQuery({
    queryKey: ['patient', patientId, 'encounters'],
    queryFn: ({ signal }) => listEncounters(patientId, signal),
  });

  const firstEncounterId = encountersQuery.data?.[0]?.id ?? '';
  const [encounterId, setEncounterId] = useState('');
  // Plain `string`, not the narrow catalog union — `DEMO_CPT_CODES[0]` is possibly `undefined` under
  // `noUncheckedIndexedAccess`, and the server re-validates against the same catalog regardless
  // (`createChargeRequestSchema`'s own `z.enum`), so narrowing here would be belt *and* the same belt.
  const [cptCode, setCptCode] = useState<string>(DEMO_CPT_CODES[0]?.code ?? '');
  const [icd10Codes, setIcd10Codes] = useState<string[]>([]);
  const [units, setUnits] = useState('1');
  // `BACKLOG.md` M3a item 2 — caller-asserted, matching `createChargeRequestSchema.selfPay`'s own
  // "this codebase has no coverage table to check against" reasoning. Unchecking the diagnosis
  // requirement below only when this is set is what makes the blocking-vs-warning distinction
  // reachable at all from this form, not just from a direct API call.
  const [selfPay, setSelfPay] = useState(false);

  const effectiveEncounterId = encounterId === '' ? firstEncounterId : encounterId;

  const create = useMutation({
    mutationFn: () =>
      createCharge(patientId, {
        encounterId: effectiveEncounterId,
        cptCode,
        icd10Codes,
        units: Number(units),
        selfPay,
        confidentialityReasons: [],
      }),
    onSuccess: () => {
      setIcd10Codes([]);
      setUnits('1');
      setSelfPay(false);
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const selectedCpt = DEMO_CPT_CODES.find((c) => c.code === cptCode);

  return (
    <section className="flex flex-col gap-2">
      <p className="m-0 text-sm text-ink-muted">{t('charges.description')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('charges.emptyMessage')}
        >
          {(charges) => (
            <DataTable
              caption={t('charges.caption')}
              columns={columnsFor(t)}
              rows={charges}
              rowKey={(charge) => charge.id}
              minWidthRem={48}
              emptyMessage={t('charges.emptyMessage')}
            />
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (effectiveEncounterId === '') return;
              if (icd10Codes.length === 0 && !selfPay) return;
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">{t('charges.encounterLabel')}</span>
              <select
                value={effectiveEncounterId}
                onChange={(event) => {
                  setEncounterId(event.target.value);
                }}
                className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                required
              >
                {(encountersQuery.data ?? []).map((encounter) => (
                  <option key={encounter.id} value={encounter.id}>
                    {encounter.encounterType} — {formatDateTime(encounter.startedAt)}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">{t('charges.cptLabel')}</span>
              <select
                value={cptCode}
                onChange={(event) => {
                  setCptCode(event.target.value);
                }}
                className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
              >
                {DEMO_CPT_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.description} (${(c.feeCents / 100).toFixed(2)})
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">{t('charges.icd10Label')}</span>
              <select
                multiple
                value={icd10Codes}
                onChange={(event) => {
                  setIcd10Codes(Array.from(event.target.selectedOptions, (option) => option.value));
                }}
                className="min-h-[6rem] rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                required={!selfPay}
              >
                {DEMO_ICD10_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.description}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={selfPay}
                onChange={(event) => {
                  setSelfPay(event.target.checked);
                }}
              />
              {t('charges.selfPayLabel')}
            </label>
            <Field
              label={t('charges.unitsLabel')}
              type="number"
              min={1}
              max={99}
              value={units}
              onChange={(event) => {
                setUnits(event.target.value);
              }}
              required
            />
            {selectedCpt !== undefined ? (
              <p className="m-0 text-sm text-ink-muted">
                {t('charges.feePreview', {
                  amount: ((selectedCpt.feeCents * Number(units || '0')) / 100).toFixed(2),
                })}
              </p>
            ) : null}
            <Button type="submit" pending={create.isPending}>
              {t('charges.recordButton')}
            </Button>
          </form>
        ) : null}
        {create.isError ? <ErrorText message={t('charges.createError')} /> : null}
      </Panel>
    </section>
  );
}
